import React, { useCallback, useEffect, useState } from 'react';
import { Card, Col, Row, Spinner as ReactSpinner } from 'react-bootstrap';
import { useParams } from 'react-router';
import { ApiBridgeAlert, ApiPerson, ApiTransaction, bridgeAlertApi } from '../apis';
import { useSelector } from 'react-redux';
import { AppState } from '../store/store';
import { useQueryAttributeMappings } from '../schema/mappings/mappingsActions';
import { PersonLink } from '../common/PersonLink';
import { isCounterparty } from '../common/helper';
import {
  InternalApiAlertType,
  InternalApiCaseAttachmentType,
  InternalApiEntityType,
  InternalApiUniversalDocumentEntityType,
} from '../api-internal';
import { Link } from 'react-router-dom';
import RFISending from '../rfi-sending/RFISending';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import './BridgeAlert.scss';
import useRfiData from '../rfi-sending/useRfiData';
import { Documents } from '../common/documents/Documents';
import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  createColumnsForObjectData,
  SalvProgressCircle,
  SalvTable,
} from '@salvapp/ui-components';
import TransactionAttributes from '../transaction/TransactionAttributes';
import PersonAttributes from '../aml/sections/PersonAttributes';
import RFIAttributes from '../rfi-sending/RFIAttributes';
import { AlertManagement } from '../alerts/management/AlertManagement';
import { getUserId } from '../auth/authReducer';
import { useAlertFlow } from '../alerts/flow/useAlertFlow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillTransfer } from '@fortawesome/pro-solid-svg-icons';
import { formatAmount } from '../schema/helper';
import { useMe } from '../organisation/useMe';
import SalvPage from '../common/SalvPage';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import Has from '../auth/Has';
import { CAN_READ_CASE } from '../auth/permissions';
import AttachmentCases from '../case/attachments/AttachmentCases';

export const BridgeAlert = () => {
  const [fetching, setFetching] = useState(true);
  const [alert, setAlert] = useState<ApiBridgeAlert | null>(null);
  const { features, currentOrganisation } = useMe();

  const activeUserId = useSelector((state: AppState) => getUserId(state));
  const alertId = Number(useParams<{ id: string }>().id);

  const [setDirty, resetBlocker, renderNavigationButtons] = useAlertFlow(
    alert?.assignee?.id == activeUserId,
    alertId,
    InternalApiAlertType.BRIDGE
  );

  const fetchAlert = async () => {
    const alert = await bridgeAlertApi.getBridgeAlert({
      id: alertId,
    });
    setAlert(alert);
  };

  const initialFetch = useCallback(async () => {
    resetBlocker();
    setFetching(true);
    try {
      await fetchAlert();
    } catch (_: unknown) {
      setAlert(null);
      return;
    } finally {
      setFetching(false);
    }

    setFetching(false);
  }, [alertId]);

  useEffect(() => {
    initialFetch();
  }, [alertId, initialFetch]);

  const isRfiEnabled =
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features) ||
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features);

  const rfiData = useRfiData(alertId, InternalApiAlertType.BRIDGE, isRfiEnabled);

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const renderLinksToEntities = (alert: ApiBridgeAlert) => {
    return (
      <div style={{ display: 'flex', gap: '24px' }}>
        {alert.person && (
          <div>
            <span className='text-secondary m-r-4'>{isCounterparty(alert.person) ? 'Counterparty:' : 'Customer:'}</span>
            <PersonLink person={alert.person} mappings={attributeMappings.map} />
          </div>
        )}
        {alert.transaction && (
          <div>
            <span className='text-secondary m-r-4'>Transaction:</span>
            <Link to={`/transactions/${alert.transaction.id}`}>{alert.transaction.id}</Link>
          </div>
        )}
      </div>
    );
  };

  const hasMultiplePersonMatches = !!alert?.personMatches && alert.personMatches.length > 0;
  const hasMultipleTransactionMatches = !!alert?.transactionMatches && alert.transactionMatches.length > 0;
  return (
    <SalvPage
      title={`Bridge investigation alert ${alertId}`}
      actions={renderNavigationButtons()}
      subTitle={alert ? renderLinksToEntities(alert) : undefined}
    >
      {fetching ? (
        <Card>
          <Card.Body>
            <div className='small'>Loading...</div>
            <ReactSpinner className='loader-spinner' animation='border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </ReactSpinner>
          </Card.Body>
        </Card>
      ) : alert !== null && alert?.id === alertId ? (
        <Row>
          {(isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features) ||
            isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features)) && (
            <Col md={4} className='sticky-col'>
              {alert.transaction && (
                <TransactionAttributes
                  transaction={alert.transaction}
                  showLinkToTransactionPage={false}
                  showLinkToCustomerPage={false}
                />
              )}
              {!alert.transaction && alert.person && <PersonAttributes person={alert.person} />}
              {!alert.transaction && !alert.person && (
                <>
                  <SalvBanner
                    accent={SalvBannerAccent.LIGHT}
                    message={
                      hasMultiplePersonMatches || hasMultipleTransactionMatches
                        ? 'Multiple entities found that could be linked with the incoming investigation'
                        : 'Could not link alert to any entity'
                    }
                    type={SalvBannerType.INFO}
                  />
                  {hasMultiplePersonMatches && (
                    <SalvTable
                      className='m-t-16'
                      columns={createColumnsForObjectData<ApiPerson>([
                        {
                          accessorKey: 'id',
                          header: 'Possible Person Links',
                          cell: (row) => <PersonLink person={row} mappings={attributeMappings.map} />,
                        },
                      ])}
                      data={alert.personMatches || []}
                    />
                  )}

                  {hasMultipleTransactionMatches && (
                    <SalvTable
                      className='matches-table mt-4'
                      columns={createColumnsForObjectData<ApiTransaction>([
                        {
                          accessorKey: 'id',
                          header: 'Possible Transaction Links',
                          cell: (row) => (
                            <Link to={`/transactions/${row.id}`}>
                              <FontAwesomeIcon icon={faMoneyBillTransfer} />{' '}
                              {formatAmount(parseFloat(row.attributes?.amount || '0'), currentOrganisation?.currency)}
                            </Link>
                          ),
                        },
                      ])}
                      data={alert.transactionMatches || []}
                    />
                  )}
                </>
              )}
              {isRfiEnabled && <RFIAttributes rfiData={rfiData} />}
            </Col>
          )}
          <Col md={8}>
            <AlertManagement
              entityType={alert.transaction ? InternalApiEntityType.TRANSACTION : InternalApiEntityType.PERSON}
              alertId={alert.id}
              alertType={InternalApiAlertType.BRIDGE}
              postAlertPropertyChange={setDirty}
            />
            {isRfiEnabled && (
              <RFISending
                alert={alert}
                alertType={InternalApiAlertType.BRIDGE}
                rfiData={rfiData}
                onChange={fetchAlert}
              />
            )}
            <Documents
              className='universal-documents--legacy'
              entityType={InternalApiUniversalDocumentEntityType.BRIDGE_ALERT}
              entityId={alertId}
            />
            {isFeatureEnabled(ORGANISATION_FEATURE.CASE_MANAGEMENT, features) && (
              <Has allPermissions={[CAN_READ_CASE]}>
                <AttachmentCases
                  attachmentType={InternalApiCaseAttachmentType.BRIDGE_ALERT}
                  externalId={String(alertId)}
                />
              </Has>
            )}
          </Col>
        </Row>
      ) : (
        <Card>
          <Card.Body>
            <div className='small'>Alert with id {alertId} does not exist.</div>
          </Card.Body>
        </Card>
      )}
    </SalvPage>
  );
};

export default BridgeAlert;
